/**
 * Contact information form component for case expenses.
 * Used when creating a new expense with MAIL or OTHER type (CONTACT payee).
 */

import { AnimatePresence, motion } from 'framer-motion';
import { User, X } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { CONTACT_LABELS } from './constants';

interface ContactInfoFormData {
  organization_name: string;
  given_name: string;
  family_name: string;
  phone_numbers: Array<{ label: string; value: string }>;
  email_addresses: Array<{ label: string; value: string }>;
  postal_addresses: Array<{
    label: string;
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  }>;
  note: string;
}

interface CaseExpenseFormData {
  contact_info: ContactInfoFormData;
  expense_type: string;
  payee_type: string;
  payee_id: string;
  amount: string;
  payment_date: string;
  payment_method: string;
  payment_method_identifier: string;
  reason: string;
  status: string;
}

/**
 * Inline contact form for creating a new contact payee.
 * Uses useFormContext to access the parent form.
 */
export const ContactInfoForm = () => {
  const { control } = useFormContext<CaseExpenseFormData>();

  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({ control, name: 'contact_info.phone_numbers' });

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({ control, name: 'contact_info.email_addresses' });

  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({ control, name: 'contact_info.postal_addresses' });

  return (
    <motion.div
      key="contact-form"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50/50"
    >
      {/* Card Header */}
      <div className="flex items-center gap-3 border-b border-zinc-100 bg-white px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-purple-600">
          <User className="h-4 w-4 text-white" />
        </div>
        <p className="text-sm font-medium text-zinc-900">New Contact</p>
      </div>

      {/* Card Body */}
      <div className="space-y-3 p-4">
        {/* Name fields - all in one row */}
        <div className="grid grid-cols-3 gap-2">
          <FormField
            control={control}
            name="contact_info.organization_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs font-medium text-zinc-600">
                  Organization
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Company"
                    className="h-9 bg-white"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="contact_info.given_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs font-medium text-zinc-600">
                  First Name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="First"
                    className="h-9 bg-white"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="contact_info.family_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs font-medium text-zinc-600">
                  Last Name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Last"
                    className="h-9 bg-white"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* Phone Numbers */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-zinc-600">Phone</span>
            <button
              type="button"
              onClick={() => appendPhone({ label: 'main', value: '' })}
              className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
            >
              + Add
            </button>
          </div>
          <AnimatePresence>
            {phoneFields.map((phoneField, index) => (
              <motion.div
                key={phoneField.id}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="group flex items-center gap-2"
              >
                <FormField
                  control={control}
                  name={`contact_info.phone_numbers.${index}.label`}
                  render={({ field: labelField }) => (
                    <Select
                      value={labelField.value}
                      onValueChange={labelField.onChange}
                    >
                      <SelectTrigger className="h-9 w-20 bg-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_LABELS.phone.map((label) => (
                          <SelectItem key={label} value={label}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <FormField
                  control={control}
                  name={`contact_info.phone_numbers.${index}.value`}
                  render={({ field: valueField }) => (
                    <Input
                      {...valueField}
                      placeholder="Phone number"
                      className="h-9 flex-1 bg-white"
                    />
                  )}
                />
                {phoneFields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePhone(index)}
                    className="p-1 text-zinc-400 opacity-0 transition-all hover:text-red-500 group-hover:opacity-100"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Email Addresses */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-zinc-600">Email</span>
            <button
              type="button"
              onClick={() => appendEmail({ label: 'work', value: '' })}
              className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
            >
              + Add
            </button>
          </div>
          <AnimatePresence>
            {emailFields.map((emailField, index) => (
              <motion.div
                key={emailField.id}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="group flex items-center gap-2"
              >
                <FormField
                  control={control}
                  name={`contact_info.email_addresses.${index}.label`}
                  render={({ field: labelField }) => (
                    <Select
                      value={labelField.value}
                      onValueChange={labelField.onChange}
                    >
                      <SelectTrigger className="h-9 w-20 bg-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_LABELS.email.map((label) => (
                          <SelectItem key={label} value={label}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <FormField
                  control={control}
                  name={`contact_info.email_addresses.${index}.value`}
                  render={({ field: valueField }) => (
                    <Input
                      {...valueField}
                      type="email"
                      placeholder="Email address"
                      className="h-9 flex-1 bg-white"
                    />
                  )}
                />
                <button
                  type="button"
                  onClick={() => removeEmail(index)}
                  className="p-1 text-zinc-400 opacity-0 transition-all hover:text-red-500 group-hover:opacity-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Postal Addresses */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-zinc-600">Address</span>
            <button
              type="button"
              onClick={() =>
                appendAddress({
                  label: 'main',
                  street: '',
                  city: '',
                  state: '',
                  postal_code: '',
                  country: 'US',
                })
              }
              className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
            >
              + Add
            </button>
          </div>
          <AnimatePresence>
            {addressFields.map((addrField, index) => (
              <motion.div
                key={addrField.id}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-1.5 rounded-lg border border-zinc-200 bg-white p-2"
              >
                <div className="flex items-center justify-between">
                  <FormField
                    control={control}
                    name={`contact_info.postal_addresses.${index}.label`}
                    render={({ field: labelField }) => (
                      <Select
                        value={labelField.value}
                        onValueChange={labelField.onChange}
                      >
                        <SelectTrigger className="h-8 w-20 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTACT_LABELS.address.map((label) => (
                            <SelectItem key={label} value={label}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => removeAddress(index)}
                    className="p-1 text-zinc-400 transition-colors hover:text-red-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <FormField
                  control={control}
                  name={`contact_info.postal_addresses.${index}.street`}
                  render={({ field: streetField }) => (
                    <Input
                      {...streetField}
                      placeholder="Street address"
                      className="h-8"
                    />
                  )}
                />
                <div className="grid grid-cols-3 gap-1.5">
                  <FormField
                    control={control}
                    name={`contact_info.postal_addresses.${index}.city`}
                    render={({ field: cityField }) => (
                      <Input
                        {...cityField}
                        placeholder="City"
                        className="h-8"
                      />
                    )}
                  />
                  <FormField
                    control={control}
                    name={`contact_info.postal_addresses.${index}.state`}
                    render={({ field: stateField }) => (
                      <Input
                        {...stateField}
                        placeholder="State"
                        className="h-8"
                      />
                    )}
                  />
                  <FormField
                    control={control}
                    name={`contact_info.postal_addresses.${index}.postal_code`}
                    render={({ field: postalField }) => (
                      <Input
                        {...postalField}
                        placeholder="ZIP"
                        className="h-8"
                      />
                    )}
                  />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Notes */}
        <FormField
          control={control}
          name="contact_info.note"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs font-medium text-zinc-600">
                Notes
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Additional notes"
                  rows={1}
                  className="min-h-[36px] resize-none bg-white"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </motion.div>
  );
};
